import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Wallet, ArrowDownCircle, ArrowUpCircle, History, Landmark, Plus, Power, Check } from "lucide-react";
import { rpc } from "@/lib/supabase";
import { PageHeader, Loader, Card, Stat, EmptyState } from "@/components/kit";
import { formatCOP, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TABS = [
  { id: "summary", label: "Resumen", icon: Wallet },
  { id: "charges", label: "Por cobrar", icon: ArrowDownCircle },
  { id: "payouts", label: "Por pagar", icon: ArrowUpCircle },
  { id: "history", label: "Historial", icon: History },
  { id: "accounts", label: "Cuentas receptoras", icon: Landmark },
];

const DOC_BADGE = {
  PENDING: { t: "Pendiente", c: "text-amber-400 border-amber-500/40 bg-amber-500/10" },
  PARTIAL: { t: "Parcial", c: "text-blue-400 border-blue-500/40 bg-blue-500/10" },
  PAID: { t: "Pagado", c: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" },
  VOID: { t: "Anulado", c: "text-gray-400 border-gray-500/40 bg-gray-500/10" },
};
const DocBadge = ({ s }) => (
  <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${DOC_BADGE[s]?.c || ""}`}>{DOC_BADGE[s]?.t || s}</span>
);

// ---------- RESUMEN ----------
const Summary = () => {
  const [d, setD] = useState(null);
  useEffect(() => { rpc("admin_finance_summary").then(setD).catch(() => setD({})); }, []);
  if (!d) return <Loader />;
  return (
    <div className="space-y-6" data-testid="finance-summary">
      <div>
        <div className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-500">Cuentas por cobrar (negocios)</div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
          <Stat label="Facturación" value={formatCOP(d.facturacion)} hint="Total generado a negocios" testId="fin-facturacion" />
          <Stat label="Recaudo" value={formatCOP(d.recaudo)} accent="#10B981" hint="Entradas cobradas" testId="fin-recaudo" />
          <Stat label="Por cobrar" value={formatCOP(d.por_cobrar)} accent="#FF6A00" hint="Saldo pendiente de negocios" testId="fin-por-cobrar" />
        </div>
      </div>
      <div>
        <div className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-500">Cuentas por pagar (conductores)</div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
          <Stat label="Obligaciones" value={formatCOP(d.obligaciones_conductores)} hint="Total a conductores" testId="fin-obligaciones" />
          <Stat label="Pagado" value={formatCOP(d.pagado_conductores)} accent="#10B981" hint="Salidas a conductores" testId="fin-pagado" />
          <Stat label="Por pagar" value={formatCOP(d.por_pagar)} accent="#FF6A00" hint="Saldo pendiente a conductores" testId="fin-por-pagar" />
        </div>
      </div>
      <div>
        <div className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-500">Comisiones y caja</div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
          <Stat label="Comisión generada" value={formatCOP(d.comision_generada)} hint="No es utilidad neta" testId="fin-comision-gen" />
          <Stat label="Comisión cobrada" value={formatCOP(d.comision_cobrada)} hint="De cargos ya pagados" testId="fin-comision-cob" />
          <Stat label="Caja (flujo)" value={formatCOP(d.caja)} accent="#60A5FA" hint="Recaudo − pagos a conductores" testId="fin-caja" />
        </div>
      </div>
    </div>
  );
};

// ---------- PAGO (cobro/pago) modal ----------
const PayDialog = ({ open, onClose, doc, direction, accounts, onDone }) => {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState(direction === "CHARGE" ? "CASH" : "CASH");
  const [accountId, setAccountId] = useState("");
  const [reference, setReference] = useState("");
  const [kind, setKind] = useState("PAYMENT");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setAmount(""); setMethod("CASH"); setAccountId(""); setReference(""); setKind("PAYMENT"); setNotes(""); }
  }, [open]);

  // Cuentas receptoras disponibles para cobros a negocio: solo activas; efectivo siempre
  const activeAccounts = (accounts || []).filter((a) => a.is_active);
  const payoutMethods = ["CASH", "BANCOLOMBIA", "NEQUI"];
  const chargeMethods = ["CASH", ...activeAccounts.filter((a) => a.method !== "CASH").map((a) => a.method)];
  const methods = direction === "CHARGE" ? [...new Set(chargeMethods)] : payoutMethods;

  const submit = async () => {
    setBusy(true);
    try {
      const fn = direction === "CHARGE" ? "admin_record_charge_payment" : "admin_record_payout_payment";
      const base = direction === "CHARGE"
        ? { p_charge_id: doc.charge_id, p_amount: Number(amount), p_method: method, p_account_id: method === "CASH" ? null : accountId || null, p_reference: reference || null, p_notes: notes || null, p_kind: kind }
        : { p_payout_id: doc.payout_id, p_amount: Number(amount), p_method: method, p_reference: reference || null, p_notes: notes || null, p_kind: kind };
      const res = await rpc(fn, base);
      toast.success(`Registrado. Saldo: ${formatCOP(res.balance)}`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(e?.message || "Error");
    } finally {
      setBusy(false);
    }
  };

  const needsAccount = direction === "CHARGE" && method !== "CASH";
  const invalid = !amount || Number(amount) === 0 || (needsAccount && !accountId) || (kind === "ADJUSTMENT" && !notes.trim());

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-[#27272A] bg-[#141416] text-white" data-testid="pay-dialog">
        <DialogHeader>
          <DialogTitle>{direction === "CHARGE" ? "Registrar cobro" : "Registrar pago"}</DialogTitle>
          <DialogDescription>
            Saldo actual: <b className="text-[#FF6A00]">{formatCOP(doc?.balance)}</b> de {formatCOP(doc?.amount)}.
            {direction === "PAYOUT" && " Confirma la transferencia/efectivo efectivamente entregado."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Button type="button" variant={kind === "PAYMENT" ? "default" : "outline"} className={kind === "PAYMENT" ? "bg-[#FF6A00] text-black" : "border-[#27272A]"} onClick={() => setKind("PAYMENT")} data-testid="kind-payment">Pago</Button>
            <Button type="button" variant={kind === "ADJUSTMENT" ? "default" : "outline"} className={kind === "ADJUSTMENT" ? "bg-[#FF6A00] text-black" : "border-[#27272A]"} onClick={() => setKind("ADJUSTMENT")} data-testid="kind-adjustment">Ajuste</Button>
          </div>
          <Input type="number" placeholder={kind === "ADJUSTMENT" ? "Importe (puede ser negativo)" : "Importe"} value={amount} onChange={(e) => setAmount(e.target.value)} className="border-[#27272A] bg-[#0d0d0f]" data-testid="pay-amount" />
          <Select value={method} onValueChange={(v) => { setMethod(v); setAccountId(""); }}>
            <SelectTrigger className="border-[#27272A] bg-[#0d0d0f]" data-testid="pay-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              {methods.map((m) => <SelectItem key={m} value={m}>{m === "CASH" ? "Efectivo" : m === "BANCOLOMBIA" ? "Bancolombia" : "Nequi"}</SelectItem>)}
            </SelectContent>
          </Select>
          {needsAccount && (
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger className="border-[#27272A] bg-[#0d0d0f]" data-testid="pay-account"><SelectValue placeholder="Cuenta receptora" /></SelectTrigger>
              <SelectContent>
                {activeAccounts.filter((a) => a.method === method).map((a) => <SelectItem key={a.id} value={a.id}>{a.label} — {a.account_masked}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Input placeholder="Referencia / comprobante (opcional)" value={reference} onChange={(e) => setReference(e.target.value)} className="border-[#27272A] bg-[#0d0d0f]" data-testid="pay-reference" />
          <Textarea placeholder={kind === "ADJUSTMENT" ? "Motivo del ajuste (obligatorio)" : "Notas (opcional)"} value={notes} onChange={(e) => setNotes(e.target.value)} className="border-[#27272A] bg-[#0d0d0f]" data-testid="pay-notes" />
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy || invalid} className="bg-[#FF6A00] text-black hover:bg-[#ff7d1a] disabled:opacity-40" data-testid="pay-submit">Registrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- POR COBRAR / POR PAGAR ----------
const Ledger = ({ direction, accounts }) => {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState({});
  const [pay, setPay] = useState(null);
  const [bizPay, setBizPay] = useState(null);
  const isCharge = direction === "CHARGE";
  const load = useCallback(() => {
    rpc(isCharge ? "admin_get_charges" : "admin_get_payouts").then((d) => setRows(d || [])).catch(() => setRows([]));
  }, [isCharge]);
  useEffect(() => { load(); }, [load]);
  if (rows === null) return <Loader />;
  if (!rows.length) return <EmptyState icon={isCharge ? ArrowDownCircle : ArrowUpCircle} title="Sin movimientos" subtitle="Aún no hay servicios entregados que generen cargos u obligaciones." testId="ledger-empty" />;

  return (
    <div className="space-y-3" data-testid={isCharge ? "charges-list" : "payouts-list"}>
      {!isCharge && (
        <div className="rounded-xl border border-[#27272A] bg-[#101012] px-4 py-3 text-xs text-gray-400" data-testid="payouts-info-note">
          Vista informativa de obligaciones por servicio. Los pagos efectivos a conductores se gestionan en <b className="text-[#FF6A00]">Pagos a conductores</b>.
        </div>
      )}
      {rows.map((g) => {
        const key = isCharge ? g.business_id : g.driver_id;
        const name = isCharge ? g.business_name : g.driver_name;
        return (
          <Card key={key} className="p-0">
            <button className="flex w-full items-center justify-between gap-3 p-4 text-left" onClick={() => setOpen((o) => ({ ...o, [key]: !o[key] }))} data-testid={`group-${key}`}>
              <div>
                <div className="font-bold text-white">{name}</div>
                <div className="text-xs text-gray-500">
                  {formatCOP(g.pagado)} pagado de {formatCOP(g.total)}
                  {!isCharge && g.payout_account && <span className="ml-2 text-gray-400">· {g.payout_account.method} {g.payout_account.account !== "••••" ? g.payout_account.account : g.payout_account.phone}</span>}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {isCharge && Number(g.pendiente) > 0 && (
                  <Button size="sm" className="bg-[#FF6A00] text-black hover:bg-[#ff7d1a]" onClick={() => setBizPay(g)} data-testid={`abono-btn-${key}`}>
                    Registrar abono
                  </Button>
                )}
                <div className="text-right">
                  <div className="font-heading text-lg font-extrabold text-[#FF6A00]">{formatCOP(g.pendiente)}</div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500">pendiente</div>
                </div>
              </div>
            </button>
            {open[key] && (
              <div className="border-t border-[#1e1e21] p-3">
                {g.items.map((it) => (
                  <div key={isCharge ? it.charge_id : it.payout_id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl px-3 py-2 hover:bg-[#101012]" data-testid={`doc-${it.service_id}`}>
                    <div className="text-xs">
                      <span className="font-mono text-gray-400">#{it.service_id.slice(0, 8)}</span>
                      <span className="ml-2 text-white">{formatCOP(it.amount)}</span>
                      {isCharge && <span className="ml-2 text-gray-500">comisión {formatCOP(it.commission)}</span>}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-400">Saldo {formatCOP(it.balance)}</span>
                      <DocBadge s={it.status} />
                      {isCharge && (it.status !== "PAID" ? (
                        <Button size="sm" className="bg-[#FF6A00] text-black hover:bg-[#ff7d1a]" onClick={() => setPay({ ...it, direction })} data-testid={`pay-btn-${it.service_id}`}>
                          Cobrar
                        </Button>
                      ) : (
                        <span className="text-emerald-400"><Check size={16} /></span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        );
      })}
      {isCharge && <PayDialog open={!!pay} onClose={() => setPay(null)} doc={pay} direction={direction} accounts={accounts} onDone={load} />}
      {isCharge && <BusinessPayDialog open={!!bizPay} onClose={() => setBizPay(null)} group={bizPay} accounts={accounts} onDone={load} />}
    </div>
  );
};

// Abono a nivel de negocio: se reparte FIFO sobre sus cargos pendientes
const BusinessPayDialog = ({ open, onClose, group, accounts, onDone }) => {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("CASH");
  const [accountId, setAccountId] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setAmount(""); setMethod("CASH"); setAccountId(""); setReference(""); setNotes(""); } }, [open]);
  const activeAccounts = (accounts || []).filter((a) => a.is_active);
  const methods = ["CASH", ...new Set(activeAccounts.filter((a) => a.method !== "CASH").map((a) => a.method))];
  const submit = async () => {
    setBusy(true);
    try {
      const res = await rpc("admin_record_business_payment", {
        p_business_id: group.business_id, p_amount: Number(amount), p_method: method,
        p_account_id: method === "CASH" ? null : accountId || null, p_reference: reference || null, p_notes: notes || null,
      });
      toast.success(`Abono aplicado a ${res.cargos_cubiertos} servicio(s). Sin aplicar: ${formatCOP(res.sin_aplicar)}`);
      onDone(); onClose();
    } catch (e) { toast.error(e?.message || "Error"); } finally { setBusy(false); }
  };
  const invalid = !amount || Number(amount) <= 0 || Number(amount) > Number(group?.pendiente) || (method !== "CASH" && !accountId);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-[#27272A] bg-[#141416] text-white" data-testid="abono-dialog">
        <DialogHeader>
          <DialogTitle>Abono de {group?.business_name}</DialogTitle>
          <DialogDescription>
            Saldo pendiente: <b className="text-[#FF6A00]">{formatCOP(group?.pendiente)}</b>. Se aplicará a los servicios más antiguos primero (FIFO), parcial a parcial.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input type="number" placeholder="Importe del abono" value={amount} onChange={(e) => setAmount(e.target.value)} className="border-[#27272A] bg-[#0d0d0f]" data-testid="abono-amount" />
          <Select value={method} onValueChange={(v) => { setMethod(v); setAccountId(""); }}>
            <SelectTrigger className="border-[#27272A] bg-[#0d0d0f]" data-testid="abono-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              {methods.map((m) => <SelectItem key={m} value={m}>{m === "CASH" ? "Efectivo" : m === "BANCOLOMBIA" ? "Bancolombia" : "Nequi"}</SelectItem>)}
            </SelectContent>
          </Select>
          {method !== "CASH" && (
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger className="border-[#27272A] bg-[#0d0d0f]" data-testid="abono-account"><SelectValue placeholder="Cuenta receptora" /></SelectTrigger>
              <SelectContent>
                {activeAccounts.filter((a) => a.method === method).map((a) => <SelectItem key={a.id} value={a.id}>{a.label} — {a.account_masked}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Input placeholder="Referencia / comprobante (opcional)" value={reference} onChange={(e) => setReference(e.target.value)} className="border-[#27272A] bg-[#0d0d0f]" data-testid="abono-reference" />
          <Textarea placeholder="Notas (opcional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="border-[#27272A] bg-[#0d0d0f]" data-testid="abono-notes" />
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy || invalid} className="bg-[#FF6A00] text-black hover:bg-[#ff7d1a] disabled:opacity-40" data-testid="abono-submit">Registrar abono</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- HISTORIAL ----------
const HistoryTab = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { rpc("admin_get_payment_history").then((d) => setRows(d || [])).catch(() => setRows([])); }, []);
  if (rows === null) return <Loader />;
  if (!rows.length) return <EmptyState icon={History} title="Sin movimientos registrados" testId="history-empty" />;
  return (
    <div className="space-y-2" data-testid="history-list">
      {rows.map((r) => (
        <Card key={r.id} className="flex flex-wrap items-center justify-between gap-3 p-3">
          <div className="text-sm">
            <span className={`font-bold ${r.direction === "CHARGE" ? "text-emerald-400" : "text-blue-400"}`}>{r.direction === "CHARGE" ? "Cobro" : "Pago"}</span>
            {r.kind === "ADJUSTMENT" && <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold text-amber-400">AJUSTE</span>}
            <span className="ml-2 text-white">{formatCOP(r.amount)}</span>
            <span className="ml-2 text-gray-500">{r.method}</span>
            {r.service_id && <span className="ml-2 font-mono text-xs text-gray-500">#{r.service_id.slice(0, 8)}</span>}
          </div>
          <div className="text-right text-xs text-gray-500">
            <div>{formatDateTime(r.created_at)} · {r.recorded_by}</div>
            {(r.reference || r.notes) && <div className="text-gray-400">{r.reference} {r.notes}</div>}
          </div>
        </Card>
      ))}
    </div>
  );
};

// ---------- CUENTAS RECEPTORAS ----------
const AccountsTab = ({ onChanged }) => {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => { rpc("admin_get_payment_accounts").then((d) => { setRows(d || []); onChanged?.(); }).catch(() => setRows([])); }, [onChanged]);
  useEffect(() => { load(); }, [load]);
  const toggle = async (a) => {
    try { await rpc("admin_set_payment_account_active", { p_id: a.id, p_active: !a.is_active }); load(); }
    catch (e) { toast.error(e?.message || "Error"); }
  };
  if (rows === null) return <Loader />;
  return (
    <div className="space-y-3" data-testid="accounts-list">
      <div className="flex justify-end">
        <Button className="bg-[#FF6A00] text-black hover:bg-[#ff7d1a]" onClick={() => setEdit({})} data-testid="account-new"><Plus size={16} className="mr-1" /> Nueva cuenta</Button>
      </div>
      {rows.map((a) => (
        <Card key={a.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`account-${a.id}`}>
          <div>
            <div className="font-bold text-white">{a.label} <span className="ml-2 rounded bg-[#18181B] px-2 py-0.5 text-[11px] text-gray-300">{a.method === "CASH" ? "Efectivo" : a.method}</span></div>
            <div className="text-xs text-gray-500">{a.holder_name || "—"} {a.has_account && `· ${a.account_masked}`}</div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${a.is_active ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-gray-500/40 bg-gray-500/10 text-gray-400"}`}>{a.is_active ? "Activa" : "Inactiva"}</span>
            {a.method !== "CASH" && <Button size="sm" variant="outline" className="border-[#27272A]" onClick={() => setEdit(a)} data-testid={`account-edit-${a.id}`}>Editar</Button>}
            <Button size="sm" variant="outline" className={a.is_active ? "border-red-500/40 text-red-400" : "border-emerald-500/40 text-emerald-400"} onClick={() => toggle(a)} data-testid={`account-toggle-${a.id}`}><Power size={14} /></Button>
          </div>
        </Card>
      ))}
      <AccountDialog open={!!edit} account={edit} onClose={() => setEdit(null)} onDone={load} />
    </div>
  );
};

const AccountDialog = ({ open, account, onClose, onDone }) => {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ method: account?.method || "BANCOLOMBIA", label: account?.label || "", holder_name: account?.holder_name || "", account_number: "", instructions: account?.instructions || "" }); }, [open, account]);
  const submit = async () => {
    setBusy(true);
    try {
      await rpc("admin_upsert_payment_account", {
        p_id: account?.id || null, p_method: f.method, p_label: f.label, p_holder_name: f.holder_name || null,
        p_account_number: f.account_number || null, p_instructions: f.instructions || null,
      });
      toast.success("Cuenta guardada (creada como inactiva; actívala cuando confirmes)");
      onDone(); onClose();
    } catch (e) { toast.error(e?.message || "Error"); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-[#27272A] bg-[#141416] text-white" data-testid="account-dialog">
        <DialogHeader><DialogTitle>{account?.id ? "Editar cuenta receptora" : "Nueva cuenta receptora"}</DialogTitle>
          <DialogDescription>Para cobros a negocios. Bancolombia/Nequi quedan inactivas hasta que las actives.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
            <SelectTrigger className="border-[#27272A] bg-[#0d0d0f]" data-testid="account-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="BANCOLOMBIA">Bancolombia</SelectItem>
              <SelectItem value="NEQUI">Nequi</SelectItem>
              <SelectItem value="CASH">Efectivo</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Nombre interno" value={f.label || ""} onChange={set("label")} className="border-[#27272A] bg-[#0d0d0f]" data-testid="account-label" />
          <Input placeholder="Titular" value={f.holder_name || ""} onChange={set("holder_name")} className="border-[#27272A] bg-[#0d0d0f]" data-testid="account-holder" />
          {f.method !== "CASH" && <Input placeholder={f.method === "NEQUI" ? "Celular" : "Número de cuenta"} value={f.account_number || ""} onChange={set("account_number")} className="border-[#27272A] bg-[#0d0d0f]" data-testid="account-number" />}
          <Textarea placeholder="Instrucciones para el cobro (opcional)" value={f.instructions || ""} onChange={set("instructions")} className="border-[#27272A] bg-[#0d0d0f]" data-testid="account-instructions" />
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy || !f.label} className="bg-[#FF6A00] text-black hover:bg-[#ff7d1a] disabled:opacity-40" data-testid="account-save">Guardar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminFinance() {
  const [tab, setTab] = useState("summary");
  const [accounts, setAccounts] = useState([]);
  const loadAccounts = useCallback(() => { rpc("admin_get_payment_accounts").then((d) => setAccounts(d || [])).catch(() => {}); }, []);
  useEffect(() => { loadAccounts(); }, [loadAccounts]);
  return (
    <div>
      <PageHeader title="Finanzas" subtitle="Cobros a negocios, pagos a conductores, comisiones y caja." testId="admin-finance" />
      <div className="mb-6 flex gap-2 overflow-x-auto pb-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`finance-tab-${t.id}`}
              className={`flex shrink-0 items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${tab === t.id ? "bg-[#FF6A00] text-black" : "border border-[#27272A] text-gray-300 hover:bg-[#18181B]"}`}>
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "summary" && <Summary />}
      {tab === "charges" && <Ledger direction="CHARGE" accounts={accounts} />}
      {tab === "payouts" && <Ledger direction="PAYOUT" accounts={accounts} />}
      {tab === "history" && <HistoryTab />}
      {tab === "accounts" && <AccountsTab onChanged={loadAccounts} />}
    </div>
  );
}
